import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PORTAL_HERO_IMAGE } from '../config/portalLandingAssets';
import { TRAINING_SPORT_LIST } from '../config/trainingSports';
import { SITE_APPS, SITE_NAME, formatAppTitle, getSiteOrigin } from '../config/siteBranding';
import { setPageMeta, resetPageMeta } from '../utils/pageMeta';
import AppSwitcher from './AppSwitcher';
import './TrainingLanding.css';

const TrainingLanding = () => {
  const { isAuthenticated, user, logout } = useAuth();

  useEffect(() => {
    setPageMeta({
      title: formatAppTitle(SITE_APPS.training.name),
      description: SITE_APPS.training.description,
      url: `${getSiteOrigin()}/training`,
    });
    return () => resetPageMeta();
  }, []);

  return (
    <div className="training-home">
      <section className="training-home__hero" aria-label={`${SITE_APPS.training.name} home`}>
        <div
          className="training-home__hero-media"
          style={{ backgroundImage: `url(${PORTAL_HERO_IMAGE})` }}
          role="img"
          aria-label="Athletes training outdoors"
        />
        <div className="training-home__hero-shade" aria-hidden="true" />

        <header className="training-home__topbar">
          <Link to="/" className="training-home__brand-link">
            {SITE_NAME}
          </Link>
          <nav className="training-home__account" aria-label="Account">
            {isAuthenticated ? (
              <>
                <span className="training-home__user">Hi, {user?.name?.split(' ')[0] || 'there'}</span>
                <button type="button" className="training-home__btn" onClick={logout}>
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="training-home__text-link">
                  Log in
                </Link>
                <Link to="/register" className="training-home__btn training-home__btn--primary">
                  Join
                </Link>
              </>
            )}
          </nav>
        </header>

        <div className="training-home__hero-copy">
          <p className="training-home__eyebrow">{SITE_NAME}</p>
          <h1 className="training-home__title">{SITE_APPS.training.name}</h1>
          <p className="training-home__tagline">
            Plans for the sport you play—pick soccer or running, each with its own trainings.
          </p>
          <div className="training-home__ctas">
            <a href="#training-sports" className="btn btn-primary training-home__cta">
              Browse sports
            </a>
            {!isAuthenticated && (
              <Link to="/register" className="btn training-home__cta training-home__cta--ghost">
                Create account
              </Link>
            )}
          </div>
          <div className="training-home__apps">
            <AppSwitcher variant="onDark" activeAppId="training" />
          </div>
        </div>
      </section>

      <section id="training-sports" className="training-home__sports" aria-labelledby="training-sports-heading">
        <div className="training-home__sports-intro">
          <h2 id="training-sports-heading">Choose your sport</h2>
          <p>Each sport has its own plans and pages. They stay separate.</p>
        </div>

        <ul className="training-home__sport-list">
          {TRAINING_SPORT_LIST.map((sport) => (
            <li key={sport.id}>
              <Link to={sport.href} className="training-home__sport-link">
                <span className="training-home__sport-name">{sport.name}</span>
                <span className="training-home__sport-tagline">{sport.tagline}</span>
                <span className="training-home__sport-meta">
                  {sport.trainings.length} trainings
                  {sport.trackerAvailable ? ' · tracker ready' : ' · plans to browse'}
                </span>
                <span className="training-home__sport-arrow" aria-hidden="true">
                  →
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default TrainingLanding;
